define([
  'immutable',
  'moment-timezone',
  'underscore',
  'color',
  'es6!sumo/slate/util/SlateUtils',
  'es6!launcher/util/listbuilder/styleTemplate',
  'es6!sumo/util',
], (Immutable, moment, _, Color, { getKeyPathsMatching }, ColorUtils, Util) => {
  const { luminance, hexToRGB } = ColorUtils;
  const CWS_TEMPLATE_ID = 'fc6fd2ad-3c99-424c-8676-ae9ac308ae7c';
  const { formatMoney } = Util;

  function getGlobals(shortcut) {
    return shortcut
      .getIn(['definition', 'meta'], Immutable.Map())
      .merge(
        shortcut
          .getIn(['definition', 'globals'], Immutable.Map())
          .keySeq()
          .reduce((m, v) => (
            m.set(v, shortcut.getIn(['definition', 'globals', v, 'value'], ''))
          ), Immutable.Map()),
      );
  }

  function formatGlobal(shortcut, format, opts = {}) {
    const { currency = Immutable.Map() } = opts;

    const definition = shortcut.get('definition');

    const symbol = currency.get('symbol', '$');
    const isPrefix = currency.get('isPrefix', true);

    const tags = {
      discount_amount: definition.getIn(['discounts', 0, 'discount_value'], 0) || 0,
      discount_min_purchase_amount: formatMoney(definition.getIn(['discounts', 0, 'min_purchase_amount']), symbol, isPrefix),
    };

    // Support everything in definition.meta
    definition.get('meta').keySeq().forEach((property) => {
      tags[property] = definition.getIn(['meta', property]);
    });

    const discountType = definition.getIn(['discounts', 0, 'discount_type']);
    if (discountType) {
      tags[`discount_type_${discountType}`] = true;

      switch (discountType) {
        case 'percentage':
          tags.discount_amount += '%';
          break;
        case 'fixed_amount':
          if (isPrefix) {
            tags.discount_amount = `${symbol}${tags.discount_amount}`;
          } else {
            tags.discount_amount += symbol;
          }

          break;
        default:
          tags.discount_amount = false;
          break;
      }
    }

    let output = format;

    // Format boolean checks first
    let regex = /{\?([0-9a-zA-Z_]+)}/g;
    let match = regex.exec(output);

    let matches = [];
    while (match !== null) {
      matches.unshift(match);
      match = regex.exec(output);
    }

    matches.forEach((m) => {
      const str = m[0];
      const variable = m[1];
      const index = m.index;
      const endIndex = index + output.substr(index).indexOf(`{/${variable}}`);
      const contentIndex = index + str.length;

      const content = output.substr(contentIndex, endIndex - contentIndex);

      if (typeof tags[variable] === 'undefined' || tags[variable] === false) {
        // Remove the whole block
        output = output.split('');
        output.splice(index, (str.length * 2) + content.length);
        output = output.join('');
      } else {
        // Leave the content in place
        output = output.split('');
        output.splice(endIndex, str.length);
        output.splice(index, str.length);
        output = output.join('');
      }
    });

    // Variable replacing
    regex = /{([0-9a-zA-Z_]+)}/g;
    match = regex.exec(output);
    matches = [];
    while (match !== null) {
      matches.unshift(match);
      match = regex.exec(output);
    }

    matches.forEach((m) => {
      const str = m[0];
      const variable = m[1];
      const index = m.index;

      if (index === 0 || output[index - 1] !== '{' || (output.length >= index + str.length && output[index + str.length] !== '}')) {
        output = output.split('');
        output.splice(index, str.length, tags[variable] || '');
        output = output.join('');
      }
    });

    // Escaped variables
    return output.replace(/{({[0-9a-zA-Z_]+})}/g, '$1');
  }

  function validateFields(components, isEmail) {
    return components.every((component) => {
      const action = component.get('action');
      const editable = component.getIn(['shortcutMeta', 'editable'], false);
      if (!editable && action !== 'redirect') return true;

      const toValue = component.get('to');
      const textValue = component.get('text');
      const componentType = component.get('type');
      let redirectIsValid = true;
      let inputFieldsAreValid = true;

      if (action === 'redirect' && componentType === 'button') {
        redirectIsValid = toValue && toValue.trim();
      }

      if (componentType === 'text' || componentType === 'button' || isEmail) {
        inputFieldsAreValid = textValue && textValue.trim();
      }

      return redirectIsValid && inputFieldsAreValid;
    });
  }

  function validateGlobals(definition, controls) {
    let globalsValid = true;
    const discount = definition.getIn(['discounts', 0], Immutable.Map());
    const discountType = discount.get('discount_type');

    controls.forEach((c) => {
      const control = definition.getIn(['globalControls', c]);
      if (!control) return;

      const customRequirements = control.get('customRequirements');
      const variables = control.get('globals');
      const output = variables.reduce((memo, v) => memo + definition.getIn(['globals', v, 'value']), '');
      if (!output) {
        globalsValid = false;
      }

      if (customRequirements && customRequirements.size) {
        customRequirements.filter((req) => {
          if (discountType === 'shipping' && req === '{discount_amount}') return false;
          return true;
        }).forEach((req) => {
          if (output.indexOf(req) === -1) {
            globalsValid = false;
          }
        });
      }
    });

    return globalsValid;
  }

  function isStepValid(props, step) {
    const {
      shortcut,
      eCommerceService,
      eCommerceServiceIntegrated,
      displayRules,
      activeEmailService,
      isECommerceValid,
      hasInvalidScopes,
    } = props;

    const templateId = shortcut.get('template_id');
    const definition = shortcut.get('definition');
    const discounts = shortcut.getIn(['definition', 'discounts'], Immutable.List());

    const stepType = step.get('type');

    let discount = Immutable.Map();
    if (discounts && discounts.size) {
      if (step.get('formTemplateId')) {
        const discountCampaignId = shortcut.getIn(['forms', step.get('formTemplateId'), 'discount_campaign_id']);

        if (discountCampaignId) {
          discount = discounts.find(d => d.get('id') === discountCampaignId) || discounts.get(0);
        }
      } else {
        // Just default to first discount
        discount = discounts.get(0);
      }
    }

    // Check specific controls
    const controls = step.get('controls', Immutable.List());
    let hasValidControls = true;

    controls.forEach((control) => {
      switch (control) {
        case 'discount-picker': {
          if (!discount) {
            hasValidControls = false;
            break;
          }

          const discountType = discount.get('discount_type');
          const discountValue = discount.get('discount_value');

          if (discountType === 'percentage' && (discountValue <= 0 || discountValue >= 100)) {
            hasValidControls = false;
          }

          if (discountType === 'fixed_amount' && discountValue <= 0) {
            hasValidControls = false;
          }

          break;
        }

        case 'collection-picker':
        case 'product-picker': {
          const [type] = control.split('-');
          const selectedProduct = definition.get('selectedProduct');
          if (!selectedProduct || !selectedProduct.getIn([type, 'id'])) {
            hasValidControls = false;
          }

          // Product MUST be selected
          if (type === 'product' && selectedProduct.getIn(['product', 'id']) === 'all') {
            hasValidControls = false;
          }

          const emailMergeData = shortcut.get('emailMergeData');

          // No merge data for email, no can save
          // TODO: we may want to figure out why no merge data exists here,
          // The product picker can be a little weird and not load merge data until user changes it
          if (!emailMergeData) hasValidControls = false;
          break;
        }

        case 'email-segmentation': {
          const campaign = shortcut.getIn(['email_campaigns', step.get('emailCampaign')], Immutable.Map());
          const rules = campaign.getIn(['segmentation', 'rules'], Immutable.List());
          if (!rules.size) {
            hasValidControls = false;
          }

          break;
        }

        default: break;
      }
    });

    if (!hasValidControls) return false;

    switch (stepType) {
      case 'ecommerce-integration': {
        const requiresIntegration = ['shopify', 'woocommerce'].indexOf(eCommerceService) > -1;
        const hasPrefix = (discount.get('discount_prefix')) ?
          discount.get('discount_prefix').trim().length : false;
        const createMethod = discount.get('create_method');

        const hasBulkDiscounts = (discount.get('bulk_discounts')) ?
          discount.get('bulk_discounts').trim().length : false;

        if (
          requiresIntegration &&
          (
            !eCommerceServiceIntegrated ||
            !isECommerceValid ||
            (hasInvalidScopes && templateId === CWS_TEMPLATE_ID)
          )
        ) {
          return false;
        }

        if (!requiresIntegration) {
          if (createMethod === 'unique' && !hasBulkDiscounts) return false;
          else if (!hasPrefix) return false;
        }

        return true;
      }

      case 'form-editor': {
        const form = shortcut.getIn(['forms', step.get('formTemplateId')]);
        const formStep = step.get('formStep');
        const controls = step.get('controls', Immutable.List());
        const hasDiscount = step.get('hasDiscount');
        const components = form.getIn([formStep, 'components']);
        const discountType = discount.get('discount_type');
        let discountIsValid = true;

        // Check discount field
        if (hasDiscount && ['percentage', 'fixed_amount'].indexOf(discountType) > -1) {
          discountIsValid = parseFloat(discount.get('discount_value'), 10) > 0;
        }

        // Validate discount values and min purchase amounts
        discounts.forEach((d, i) => {
          const currentValue = d.get('discount_value');
          const currentMinAmount = d.get('min_purchase_amount');
          if (
            currentValue < 1
            || (currentMinAmount < 0 && typeof currentMinAmount !== 'undefined')
            || isNaN(currentValue)
            || (isNaN(currentMinAmount) && typeof currentMinAmount !== 'undefined')
          ) {
            discountIsValid = false;
            return false;
          }

          if (i === 0) return true;

          if (controls && controls.contains('discount-tiers')) {
            const prevDiscount = discounts.get(i - 1);

            if (prevDiscount.get('discount_value') >= currentValue) {
              discountIsValid = false;
              return false;
            }

            if (prevDiscount.get('min_purchase_amount') >= currentMinAmount) {
              discountIsValid = false;
              return false;
            }
          }

          return true;
        });

        return (
          discountIsValid
          && validateGlobals(definition, controls)
          && validateFields(components)
        );
      }

      case 'email-editor': {
        const emailTemplateId = step.get('emailTemplateId');
        const emailFields = shortcut.getIn(['emails', emailTemplateId]);
        const variableFields = emailFields.get('variables');
        const fromText = emailFields.getIn(['from', 'text']);
        const subjectText = emailFields.getIn(['subject', 'text']);
        const variantToggle = step.get('variantToggle');
        const variants = shortcut.getIn(['definition', 'variants'], Immutable.List());
        const disabled = variantToggle && !variants.contains(variantToggle);

        return (
          fromText
          && fromText.trim()
          && subjectText
          && subjectText.trim()
          && validateFields(variableFields, true)
        ) || disabled;
      }

      case 'display-settings': {
        const form = shortcut.getIn(['forms', step.get('formTemplateId')]);
        const campaignId = form.get('campaign_id');
        const ruleGroups = displayRules.get(campaignId, Immutable.Map());

        return ruleGroups.valueSeq().reduce((memo, rules) => (
          memo.concat(rules.filter(rule => (
            rule.get('filter_type') === 'containing' &&
            rule.get('rule_type') === 'include'
          )))
        ), Immutable.List()).size;
      }

      case 'esp-integration': {
        if (activeEmailService && ['sumo', 'other'].indexOf(activeEmailService.id) === -1) {
          return activeEmailService.connected;
        }

        return true;
      }

      case 'show-settings': {
        const formTemplateId = step.get('formTemplateId');
        const form = shortcut.getIn(['forms', formTemplateId]);

        return form.get('before_show_delay') > 0 && form.get('cookie_lifetime') > 0;
      }

      case 'min-cart-value-settings': {
        const formTemplateId = step.get('formTemplateId');
        const formStep = step.get('formStep');
        const minCartValue = shortcut.getIn(['forms', formTemplateId, formStep, 'min_cart_value'], 0);
        const max = shortcut.getIn(['definition', 'discounts', step.get('discountIndex'), 'min_purchase_amount'], 0);

        if (!minCartValue || minCartValue <= 0 || minCartValue >= max) return false;
        return true;
      }

      case 'email-settings': {
        const emailCampaign = step.get('emailCampaign');

        // Validate time for scheduled email
        const scheduledFor = shortcut.getIn(['email_campaigns', emailCampaign, 'settings', 'scheduled_for']);

        if (scheduledFor && scheduledFor.get('value') === 'custom') {
          const currentTime = moment();
          const scheduledTime = moment(scheduledFor.get('time'));

          if (!scheduledTime.isValid() || currentTime.isAfter(scheduledTime)) {
            return false;
          }
        }

        return true;
      }

      default: return true;
    }
  }

  function mergeTags(props, tags, item) {
    const {
      currency = Immutable.Map(),
      discount,
    } = props;
    let mergedItem = item;

    if (discount) {
      const symbol = currency.get('symbol', '$');
      const isPrefix = currency.get('isPrefix', true);

      const discountValue = discount.get('discount_value');
      const discountType = discount.get('discount_type');

      switch (discountType) {
        case 'percentage':
          tags.discount_text = `${discountValue}% off`;
          break;
        case 'shipping':
          tags.discount_text = 'Free Shipping';
          break;
        default:
          tags.discount_text = `${formatMoney(discountValue, symbol, isPrefix)} off`;
          break;
      }
    }

    Object.keys(tags).forEach((tag) => {
      const reg = new RegExp(`{${tag}}`, 'g');
      const value = tags[tag];
      mergedItem = mergedItem.replace(reg, value);
    });

    return mergedItem;
  }

  /**
  * Checks that all of the variants in `variants` are present in `shortcutVariants`
  *
  * @param shortcutVariants - Immutable.List()
  * @param variants - Immutable.List()
  */
  function hasValidVariants(shortcutVariants, variants) {
    const validVariants = variants.filter(v => shortcutVariants.contains(v));
    if (validVariants.size !== variants.size) return false;
    return true;
  }

  function replaceEmailSlugs(slug, value, body = '') {
    const newSlug = slug
      .replace(/\[/g, '\\[')
      .replace(/\]/g, '\\]');

    const newValue = (typeof value === 'string' ? value : '')
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"')
      .replace(/\\\\n/g, '\\n')
      .replace(/\r?\n/g, '\\n')
      .replace(/\\\\t/g, '\\t');

    return body.replace(new RegExp(newSlug, 'g'), newValue || ' ');
  }

  const updateNodeVisibility = (nodes, id, setting) => {
    return nodes.map((node) => {
      const childNodes = node.get('nodes');
      const nodeId = node.getIn(['data', 'id']);
      const hasShowNode = node.hasIn(['data', 'showNode']);
      let retNode = node;

      if (hasShowNode && nodeId === id) {
        retNode = retNode.setIn(['data', 'showNode'], setting);
      }

      if (childNodes) {
        retNode = retNode.set('nodes', updateNodeVisibility(childNodes, id, setting));
      }

      return retNode;
    });
  };

  const hasGroupSlug = (step, control) => {
    const groupSlug = step.has('group_slug');
    const stepGroupSlug = step.get('group_slug');
    const controlGroupSlug = control.get('group_slug');

    return (groupSlug && stepGroupSlug !== controlGroupSlug);
  };

  const hasFeatures = ({ features, shortcutVariants }, requiredFeatures) => {
    if (!requiredFeatures || !requiredFeatures.size ) return true;

    const requiresFeature = key => (
      requiredFeatures.get(key) === true ||
      hasValidVariants(shortcutVariants, requiredFeatures.get(key))
    );

    const featureKeys = requiredFeatures.keySeq().toArray();

    const requiredFeaturesCount = featureKeys.reduce((count, key) => {
      if (requiresFeature(key)) return count + 1;
      return count;
    }, 0);

    const validFeatures = featureKeys.filter(key => (
      features.get(key) === requiresFeature(key)
    ));

    return validFeatures.length === requiredFeaturesCount;
  };

  // Sort timzones by offset.
  const timezones = _.map(moment.tz.names(), tz => ({
    tz,
    name: tz.replace('_', ' '),
    offset: moment.tz(tz).format('Z'),
  })).sort((a, b) => {
    const intA = parseInt(a.offset.replace(':', ''), 10);
    const intB = parseInt(b.offset.replace(':', ''), 10);
    return intA - intB;
  });

  const timezoneOptions = _.map(timezones, tz => (
    { label: `(GMT ${tz.offset}) ${tz.name}`, value: tz.name }
  ));

  const getTimeOptions = () => {
    const hourOptions = [];
    const minuteOptions = [];
    const meridiemOptions = [
      { label: 'AM', value: 'am' },
      { label: 'PM', value: 'pm' },
    ];

    for (let i = 0; i < 4; i += 1) {
      const minute = `0${i * 15}`.slice(-2);
      minuteOptions.push({ label: minute, value: minute });
    }

    for (let i = 1; i < 13; i += 1) {
      const hour = `0${i}`.slice(-2);
      hourOptions.push({ label: hour, value: hour });
    }

    return {
      minute: minuteOptions,
      hour: hourOptions,
      meridiem: meridiemOptions,
    };
  };

  const getColorRGBArray = (color) => {
    // Fallback when auto creating colors
    if (!color) return [0, 0, 0, 1];

    if (typeof color === 'string' && color.indexOf('#') === 0) {
      return hexToRGB(color);
    }
    return color.replace(/[^\d,]/g, '').split(',');
  };

  const getAchromaticColor = (color) => {
    const colorRGBArray = getColorRGBArray(color);
    const isDarkColor = (luminance(colorRGBArray) < 200);
    const isHexColor = (color.indexOf('#') === 0);
    let newTextColor;

    if (isDarkColor) {
      newTextColor = isHexColor ? '#FFFFFF' : 'rgba(255,255,255,1)';
    } else {
      newTextColor = isHexColor ? '#000000' : 'rgba(0,0,0,1)';
    }

    return newTextColor;
  };

  const updateShortcutColor = (shortcut, { key, color }) => {
    const colorRGBArray = getColorRGBArray(color);

    // update the globals
    let retShortcut = shortcut.setIn(['definition', 'meta', key], color);

    // Detect optimal text color
    const textColor = getAchromaticColor(color);

    const textColors = retShortcut.getIn(
      ['definition', 'meta', 'textColors'],
      Immutable.Map({ buttonTextColor: 'buttonColor' }),
    );

    // Update all text colors relying on this color
    textColors.keySeq().forEach((c) => {
      const baseColor = textColors.get(c);
      if (baseColor !== key) return;
      retShortcut = retShortcut.setIn(['definition', 'meta', c], textColor);
    });

    // Update modified colors (colors based on others)
    const modifiedColors = retShortcut.getIn(['definition', 'meta', 'modifiedColors'], Immutable.Map());
    const disableGradient = shortcut.getIn(['definition', 'variants'], Immutable.List())
      .includes('disabled_color_gradient');

    modifiedColors.keySeq().forEach((c) => {
      const baseColorName = modifiedColors.getIn([c, 0]);
      if (baseColorName !== key) return;

      const modifiers = modifiedColors
        .getIn([c, 1], '1,1,1,1')
        .split(',')
        .map(v => parseFloat(v));

      const updatedColor = `rgba(${colorRGBArray.map((v, i) => {
        const val = parseFloat(v) * modifiers[i];
        if (i === 3) {
          if (disableGradient) {
            return 1;
          }
          return val;
        }

        return Math.floor(val);
      }).join(',')})`;

      retShortcut = retShortcut.setIn(['definition', 'meta', c], updatedColor);
    });

    // update email colors
    const emails = retShortcut.get('emails');

    emails.forEach((email, emailTemplateId) => {
      let nextBody = email.get('body');
      const isSlateJson = typeof nextBody !== 'string';

      // TODO: update to support different color value variables
      if (nextBody && isSlateJson && key === 'buttonColor') {
        const keyPathToButton = getKeyPathsMatching(nextBody, { type: 'button' })[0];
        const keyPathToLink = getKeyPathsMatching(nextBody, { type: 'link' })[0];

        if (keyPathToButton) {
          const lastStyle = nextBody.getIn(keyPathToButton.concat(['data', 'style']));
          const nextStyle = lastStyle && lastStyle.toJS ? lastStyle.toJS() : {};

          // Why are we using color here instead of luminance?
          nextStyle.borderColor = Color.lightOrDark(color) === 'dark' ? 'rgba(255,255,255,1)' : 'rgba(240,240,240,1)';
          nextStyle.backgroundColor = color;
          nextStyle.color = textColor;

          nextBody = nextBody.setIn(
            keyPathToButton.concat(['data', 'style']),
            Immutable.Map(nextStyle),
          );
        }

        if (keyPathToLink) {
          const lastStyle = nextBody.getIn(keyPathToLink.concat(['data', 'style']));
          const nextStyle = lastStyle && lastStyle.toJS ? lastStyle.toJS() : {};

          // Why are we using color here instead of luminance?
          let linkColor;
          if (Color.lightOrDark(color) === 'dark') linkColor = color;
          nextStyle.color = linkColor;
          nextBody = nextBody.setIn(
            keyPathToLink.concat(['data', 'style']),
            Immutable.Map(nextStyle),
          );
        }
      }

      retShortcut = retShortcut.setIn(['emails', emailTemplateId, 'body'], nextBody);
    });

    return retShortcut;
  };

  /**
   * Deals with rounding issues to match Shopify
   */
  const getTruncatedPrice = (amount) => {
    const amountString = `${amount}`;
    const indexOfDot = amountString.indexOf('.');

    return +`${amountString.substring(0, indexOfDot)}.${amountString.substr(indexOfDot + 1, 2)}`;
  };

  /**
   * Returns the email merge data for a given shortcut
   */
  const getEmailMergeDataForShortcut = (shortcut) => {
    const emailMergeData = {};
    const productMergeData = shortcut.get('emailMergeData');
    const definition = shortcut.get('definition');
    const hasIncludedDiscount = definition.get('variants', Immutable.List()).includes('include-discount');

    if (productMergeData) {
      Object.assign(emailMergeData, productMergeData.toJSON());

      // augment product data with discounted prices
      if (hasIncludedDiscount) {
        const discount = definition.getIn(['discounts', 0]);
        const discountType = discount.get('discount_type');
        const discountValue = discount.get('discount_value');

        emailMergeData.has_discount = true;

        if (discountType === 'percentage' || discountType === 'fixed_amount') {
          const isPercentageDiscount = (discountType === 'percentage');
          productMergeData.forEach((value, mergeKey) => {
            if (mergeKey.includes('_price')) {
              const price = +value.replace(/[^\d.]/g, '');
              const discountedMergeKey = mergeKey.replace('_price', '_discounted_price'); // product_price -> product_discounted_price
              const discountedAmount = Math.max(0, (
                isPercentageDiscount
                ? (price - getTruncatedPrice(price * (discountValue / 100)))
                : (price - discountValue)
              ));
              const formattedDiscountAmount = `$${discountedAmount.toFixed(2)}`;

              emailMergeData[discountedMergeKey] = formattedDiscountAmount;
            }
          });
        }
      }
    }

    const emailCampaign = 'customerOutreachEmail';
    const emailTypeSlug = shortcut.getIn(['email_campaigns', emailCampaign, 'settings', 'email_type', 'value']);
    if (emailTypeSlug) {
      const emailTypeId = emailTypeSlug.replace(/-([a-z])/g, g => g[1].toUpperCase());
      const footerAltEnabled = shortcut.getIn(['emails', emailTypeId, 'footer_alt_text', 'enabled']);
      emailMergeData.SUMO_CUSTOM_FOOTER = footerAltEnabled;
    }

    return emailMergeData;
  };

  const generateColorPalette = (name, color) => {
    // TODO - Add dark and light version of colors as well
    const capName = name.charAt(0).toUpperCase() + name.slice(1);
    const achromaticKey = `achromatic${capName}`;
    const achromaticColor = getAchromaticColor(color);

    return Immutable.fromJS({ [achromaticKey]: achromaticColor });
  };

  return {
    formatGlobal,
    generateColorPalette,
    getGlobals,
    hasFeatures,
    hasGroupSlug,
    hasValidVariants,
    isStepValid,
    mergeTags,
    replaceEmailSlugs,
    getAchromaticColor,
    getTimeOptions,
    timezoneOptions,
    updateNodeVisibility,
    updateShortcutColor,
    validateFields,
    getEmailMergeDataForShortcut,
  };
});
